import { HealerEnemy } from "./enemies/HealerEnemy";
import { Enemy} from "./enemies/Enemy";
import { Turret } from "./turrets/Turret";
import { Arrow } from "./turrets/shots/Arrow";
import { GlueBullet } from "./turrets/shots/GlueBullet";
import { Mortar } from "./turrets/shots/Mortar";
import { Mine } from "./turrets/shots/Mine";
import { Glue } from "./turrets/shots/Glue";
import { TeleportTurret } from "./turrets/TeleportTurret";
import { EventDispatcher } from "./events/EventDispatcher";
import { Event } from "./events/Event";
import { EnemiesSpawner } from "./EnemiesSpawner";
import { GameConstants } from "./GameConstants";
import { MathUtils } from "./utils/MathUtils";
import { CrossbowTurret } from "./turrets/CrossbowTurret";
import { MortarTurret } from "./turrets/MortarTurret";
import { SplatTurret } from "./turrets/SplatTurret";
import { BzztTurret } from "./turrets/BzztTurret";
import { FireTurret } from "./turrets/FireTurret";
import { MinesTurret } from "./turrets/MinesTurret";
import { RocketsTurret } from "./turrets/RocketsTurret";
import { SuicideSoldier } from "./SuicideSoldier";

import * as Types from "./Types";
import { WindEnemy } from "./enemies/WindEnemy";

export class Engine {

    public waveActivated: boolean;
    public turrets: Turret[];
    public enemySpawningDeltaTicks: number;
    public lastWaveTick: number;
    public enemyData: any;
    public turretData: any;
    public wavesData: any;
    public modifiers: any;
    public waveEnemies: any;
    public waveReward: number;
    public remainingReward: number;
    public enemies: Enemy[];
    public enemiesPathsCells: {name: string, path: {r: number, c: number}[]}[];
    public plateausCells: {r: number, c: number} [];
    public turretId: number;
    public enemyId: number;
    public bulletId: number;
    public mortarId: number;
    public glueId: number;
    public mineId: number;
    public waveDefaultHealth: number;
    public enemyHealthModifier: number;
    public enemyRewardModifier: number;
    public initialTimer: number;
    public boardSize: {r: number, c: number};
    public suicideSoldierCooldownCounter: number;
    public suicideSoldierCoolingDown: boolean;
    public freezeAttackCooldownCounter: number;
    public freezeAttackDuration: number;
    public freezeAttackCoolingDown: boolean;
    public freezeAttackActive: boolean;
    public eventDispatcher: EventDispatcher;
    public mines: Mine[];
    public initialTicksTimer: number;

    private runningInClientSide: boolean;
    private _version: string;
    private _credits: number;
    private _creditsEarned: number;
    private _score: number;
    private _lifes: number;
    private _paused: boolean;
    private _timeStep: number;
    private _gameOver: boolean;
    private _win: boolean;
    private _round: number;
    private _ticksCounter: number;
    private _bonus: number;
    private arrows: Arrow[];
    private glueBullets: GlueBullet[];
    private mortars: Mortar[];
    private glues: Glue[];
    private arrowsColliding: Arrow[];
    private glueBulletsColliding: GlueBullet[];
    private mortarsImpacting: Mortar[];
    private minesImpacting: Mine[];
    private consumedGlues: Glue[];
    private teleportedEnemies: {enemy: Enemy, teleportTurret: TeleportTurret}[];
    private t: number;
    private enemiesSpawner: EnemiesSpawner;
    private noEnemiesOnStage: boolean;
    private waveEnemiesLength: number;
    private enemiesSpawned: number;
    private allEnemiesSpawned: boolean;
    private canLaunchNextWave: boolean;
    private suicideAttackDamage: number;
    private suicideSoldier: SuicideSoldier;
    private suicideAttackCooldownTicks: number;
    private freezeAttackCooldownTicks: number;
    private needInitialTimer: boolean;
    
    constructor (gameConfig: Types.GameConfig, enemyData: any, turretData: any, wavesData: any, modifiers: any) {

        this._version = GameConstants.VERSION;

        this.turretId = 0;
        this.enemyId = 0;
        this.bulletId = 0;
        this.mortarId = 0;
        this.glueId = 0;
        this.mineId = 0;

        this.initialTicksTimer = 400;

        this.needInitialTimer = gameConfig.initialTimer;

        this.enemySpawningDeltaTicks = gameConfig.enemySpawningDeltaTicks;
        this.runningInClientSide = gameConfig.runningInClientSide;
        this.boardSize = gameConfig.boardSize;
        this._credits = gameConfig.credits;
        this._lifes = gameConfig.lifes;
        this._paused = false;
        this._timeStep = gameConfig.timeStep;

        this.enemiesPathsCells = gameConfig.enemiesPathsCells;
        this.plateausCells = gameConfig.plateausCells;

        this.enemyData = enemyData;
        this.turretData = turretData;
        this.wavesData = wavesData;
        this.modifiers = modifiers;

        this._score = 0;
        this._gameOver = false;
        this._win = false;
        this._round = 0;
        this._bonus = 0;

        this._creditsEarned = 0;
        this.enemyHealthModifier = 1;
        this.enemyRewardModifier = 1;

        this.waveActivated = false;
        this.t = 0;

        this.initialTimer = 0;

        this.eventDispatcher = new EventDispatcher();
        this.enemiesSpawner = new EnemiesSpawner(this);
        
        this._ticksCounter = 0;
        this.lastWaveTick = 0;

        this.turrets = [];
        this.mines = [];
        this.minesImpacting = [];
        this.waveEnemies = [];

        this.suicideAttackDamage = gameConfig.suicideAttackDamage;
        this.suicideSoldier = null;
        this.suicideSoldierCooldownCounter = 0;
        this.suicideSoldierCoolingDown = false;
        this.suicideAttackCooldownTicks = gameConfig.suicideAttackCooldownTicks;

        this.freezeAttackCooldownCounter = 0;
        this.freezeAttackDuration = gameConfig.freezeAttackDuration;
        this.freezeAttackCoolingDown = false;
        this.freezeAttackCooldownTicks = gameConfig.freezeAttackCooldownTicks;
        this.freezeAttackActive = false;

        this.canLaunchNextWave = true;

        this.initWaveVars();
        this.noEnemiesOnStage = false;
        this.allEnemiesSpawned = false;
        this.enemiesSpawned = 0;
        this.waveEnemiesLength = 0;

        this.remainingReward = 0;
    }

    public initWaveVars(): void {

        this.t = Date.now();

        this.enemies = [];
        
        this.arrows = [];
        this.glueBullets = [];
        this.mortars = [];
        this.glues = [];

        this.arrowsColliding = [];
        this.glueBulletsColliding = [];
        this.mortarsImpacting = [];
        this.consumedGlues = [];
        this.teleportedEnemies = [];
    }

    public update(): void {

        if (this.runningInClientSide) {

            const t = Date.now();

            if (t - this.t < this._timeStep) {
                return;
            }

            this.t = t;
        }

        if (this._paused) {
            return;
        }

        if (this.initialTimer < this.initialTicksTimer && this.needInitialTimer) {

            this.initialTimer++;

            if (this.initialTimer === this.initialTicksTimer) {
                this.newWave();
            }
        }

        if (this._lifes <= 0 && !this._gameOver) {

            this.eventDispatcher.dispatchEvent(new Event(Event.GAME_OVER));
            this._gameOver = true;

            // console.log("TICKS: " + this._ticksCounter);
            // console.log("SCORE: " + this._score);
        }

        if (this.noEnemiesOnStage && this.allEnemiesSpawned && this.arrows.length === 0 && this.glueBullets.length === 0 && this.glues.length === 0 && this.mortars.length === 0) {

            this.waveActivated = false;

            if (this._lifes > 0 && !this._win) {

                if (this._round >= Object.keys(this.wavesData).length) {
                    this._win = true;
                }

                this.eventDispatcher.dispatchEvent(new Event(Event.WAVE_OVER));
                
            } else {
                // return;
            } 
        }

        if (this.allEnemiesSpawned && !this.canLaunchNextWave && !this._gameOver) {
            if (Object.keys(this.wavesData).length > this._round) {
                this.canLaunchNextWave = true;
                this.initialTimer = 0;
                this.needInitialTimer = true;
                this.eventDispatcher.dispatchEvent(new Event(Event.ACTIVE_NEXT_WAVE));
            }
        }

        if (this.waveActivated) {
            this.removeProjectilesAndAccountDamage();
        }

        this.teleport();

        this.checkCollisions();
        this.spawnEnemies();

        this.enemies.forEach(function (enemy) {
            enemy.update();
        }, this); 

        this.turrets.forEach(function (turret) {
            turret.update();
        }); 

        this.arrows.forEach(function (bullet) {
            bullet.update();
        }); 

        this.glueBullets.forEach(function (bullet) {
            bullet.update();
        }); 

        this.mortars.forEach(function (mortars) {
            mortars.update();
        });

        this.mines.forEach(function (mine) {
            mine.update();
        });

        this.glues.forEach(function (glue) {
            glue.update();
        });

        if (this.suicideSoldier) {
            this.suicideSoldier.update();
        }

        if (this.suicideSoldierCoolingDown) {

            this.suicideSoldierCooldownCounter ++;

            if (this.suicideSoldierCooldownCounter === this.suicideAttackCooldownTicks) {
                this.suicideSoldierCoolingDown = false;
                this.eventDispatcher.dispatchEvent(new Event(Event.SUICIDE_SOLDIER_AVAILABLE));
            }
        }

        if (this.freezeAttackCoolingDown) {

            this.freezeAttackCooldownCounter ++;

            if (this.freezeAttackActive && this.freezeAttackCooldownCounter === this.freezeAttackDuration) {
                this.freezeAttackActive = false;
                this.eventDispatcher.dispatchEvent(new Event(Event.FREEZE_ATTACK_ENDED));
            }

            if (this.freezeAttackCooldownCounter === this.freezeAttackCooldownTicks) {
                this.freezeAttackCoolingDown = false;
                this.eventDispatcher.dispatchEvent(new Event(Event.FREEZE_ATTACK_AVAILABLE));
            }
        }

        this._ticksCounter ++;
    }

    public newWave(noExtraCoins?: boolean): boolean {

        if (!this.canLaunchNextWave) {
            return false;
        }

        let perc = 1 - (this.initialTimer / this.initialTicksTimer);
        let extraCredits = Math.round(50 * Math.round(perc * 10 ) / 10);
        if (!this.needInitialTimer || noExtraCoins) {
            extraCredits = 0;
        }

        this._credits += extraCredits;

        this.initialTicksTimer = 200;
        this.initialTimer = this.initialTicksTimer;

        this.canLaunchNextWave = false;

        this.noEnemiesOnStage = false;
        this.allEnemiesSpawned = false;

        let length = Object.keys(this.wavesData).length;
        let waveData = this.wavesData["wave_" + (this._round % length + 1)];
        
        let initialWaveEnemies = waveData.enemies.slice(0);

        let newWaveEnemies = JSON.parse(JSON.stringify(initialWaveEnemies));

        this._round++;

        for (let i = 0; i < newWaveEnemies.length; i++) {
            newWaveEnemies[i].t = newWaveEnemies[i].t * this.enemySpawningDeltaTicks + this._ticksCounter + 1;
        }

        this.waveEnemies = this.waveEnemies.concat(newWaveEnemies);
        this.waveEnemies = MathUtils.mergeSort(this.waveEnemies, function(e1: any , e2: any): boolean { return e1.t - e2.t < 0; });

        this.lastWaveTick = this._ticksCounter;
        
        this.waveReward = waveData.waveReward;

        this.waveActivated = true;

        this.waveEnemiesLength += newWaveEnemies.length;

        this.waveDefaultHealth = 0;
        for (let i = 0; i < this.waveEnemies.length; i++) {
            this.waveDefaultHealth += this.enemyData[this.waveEnemies[i].type][this.waveEnemies[i].subtype].life;
            this.remainingReward += Math.round(this.enemyRewardModifier * this.enemyData[this.waveEnemies[i].type][this.waveEnemies[i].subtype].value);
        }

        let damagePossible = Math.round(GameConstants.DIFFICULTY_LINEAR * this._creditsEarned + GameConstants.DIFFICULTY_MODIFIER * Math.pow(this._creditsEarned, GameConstants.DIFFICULTY_EXPONENT));
        let healthModifier = MathUtils.fixNumber(damagePossible / this.waveDefaultHealth);
        healthModifier = Math.max(healthModifier, GameConstants.MIN_HEALTH_MODIFIER);

        let rewardModifier = GameConstants.REWARD_MODIFIER * Math.pow(healthModifier, GameConstants.REWARD_EXPONENT);
        rewardModifier = Math.max(rewardModifier, GameConstants.MIN_REWARD_MODIFIER);

        this.enemyHealthModifier = healthModifier;
        this.enemyRewardModifier = rewardModifier;

        this._bonus = Math.round(this.waveReward + Math.round(GameConstants.EARLY_BONUS_MODIFIER * Math.pow(Math.max(0, this.remainingReward), GameConstants.EARLY_BONUS_EXPONENT)));

        this.eventDispatcher.dispatchEvent(new Event(Event.NEW_WAVE, [extraCredits]));

        return true;
    }

    public removeEnemy(enemy: Enemy): void {

        const i = this.enemies.indexOf(enemy);

        if (i !== -1) {
            this.enemies.splice(i, 1);
        }

        enemy.destroy();
    }

    public addTurret(type: string, p: {r: number, c: number}, skipBuild?: boolean, level?: number): Turret {

        // mirar si estamos poniendo la torreta encima del camino
        for (let i = 0; i < this.enemiesPathsCells.length; i++) {
            for (let j = 0; j < this.enemiesPathsCells[i].path.length; j++) {
                if (p.c === this.enemiesPathsCells[i].path[j].c && p.r === this.enemiesPathsCells[i].path[j].r) {
                    return null;
                }
            }
        }

        // mirar si ya hay una torreta
        for (let i = 0; i < this.turrets.length; i++) {
            if (p.c === this.turrets[i].position.c && p.r === this.turrets[i].position.r) {
                return null;
            }
        }

        let isOnPlateau = false;

        // miramos si esta en una celda en la que se puede posicionar
        if (this.plateausCells.length !== 0) {
            for (let i = 0; i < this.plateausCells.length; i++) {
                if (this.plateausCells[i].c === p.c && this.plateausCells[i].r === p.r) {
                    isOnPlateau = true;
                    break;
                }
            }
        } else {
            isOnPlateau = true;
        }

        if (!isOnPlateau) {
            return null;
        }

        let turret: Turret = null;

        switch (type) {
            case GameConstants.TURRET_PEA:
                turret = new CrossbowTurret(p, this, skipBuild, level);
                break;
            case GameConstants.TURRET_MORTAR:
                turret = new MortarTurret(p, this, skipBuild, level);
                break;
            case GameConstants.TURRET_SPLAT:
                turret = new SplatTurret(p, this, skipBuild, level);
                break;
            case GameConstants.TURRET_BZZT:
                turret = new BzztTurret(p, this, skipBuild, level);
                break;
            case GameConstants.TURRET_FIRE:
                turret = new FireTurret(p, this, skipBuild, level);
                break;
            case GameConstants.TURRET_MINES:
                turret = new MinesTurret(p, this, skipBuild, level);
                break;
            case GameConstants.TURRET_ROCKETS:
                turret = new RocketsTurret(p, this, skipBuild, level);
                break;
            case GameConstants.TURRET_TELEPORT:
                turret = new TeleportTurret(p, this, skipBuild, level);
                break;
            default:
        }

        if (this._credits < turret.value && !skipBuild) {
            return null;
        }

        this.turrets.push(turret);

        if (!skipBuild) {
            this._credits -= turret.value;
        }     

        return turret;
    }

    public sellTurret(id: number): boolean {

        const turret = this.getTurretById(id);

        if (!turret) {
            return false;
        }

        const i = this.turrets.indexOf(turret);

        if (i !== -1) {
            this.turrets.splice(i, 1);
        }

        this._credits += turret.value * .8;
        turret.destroy();

        return true;
    }

    public launchFreezeAttack(): boolean {

        if (this.freezeAttackCoolingDown) {
            return false;
        }

        this.freezeAttackCooldownCounter = 0;
        this.freezeAttackCoolingDown = true;
        this.freezeAttackActive = true;
        
        return true;
    }

    public launchSuicideAttack(): boolean {

        if (this.suicideSoldierCoolingDown) {
            return false;
        }

        let pathIndex = 0;

        let distanceToExit = 1e5;
        let enemyClosestToExit: Enemy;

        for (let i = 0; i < this.enemies.length; i ++) {

            const anutoEnemy = this.enemies[i];

            const d = anutoEnemy.path.length - anutoEnemy.l;

            if (d < distanceToExit) {
                distanceToExit = d;
                enemyClosestToExit = this.enemies[i];
            }
        }

        if (enemyClosestToExit) {
            for (let i = 0; i < this.enemiesPathsCells.length; i ++) {
                if (this.enemiesPathsCells[i].path === enemyClosestToExit.path) {
                    pathIndex = i;
                    break;
                }
            }
        }

        const path = this.enemiesPathsCells[pathIndex].path;

        this.suicideSoldier = new SuicideSoldier(this, path);
        
        this.eventDispatcher.dispatchEvent(new Event(Event.SUICIDE_SOLDIER_SPAWNED, [this.suicideSoldier]));

        return true;
    }

    public onSuicideSoldierReachedExit(): void {

        this.suicideSoldierCooldownCounter = 0;
        this.suicideSoldierCoolingDown = true;

        this.suicideSoldier.destroy();
        this.suicideSoldier = null;

        this.eventDispatcher.dispatchEvent(new Event(Event.SUICIDE_SOLDIER_REACHED_EXIT, [this.suicideSoldier]));
    }

    public setNextStrategy(id: number): boolean {

        const turret = this.getTurretById(id);

        if (turret) {
            turret.setNextStrategy();
            return true;
        }

        return false;
    }

    public setFixedTarget(id: number): boolean {

        const turret = this.getTurretById(id);

        if (turret) {
            turret.setFixedTarget();
            return true;
        }

        return false;
    }

    public addBullet(bullet: Arrow, peaTurret: CrossbowTurret): void {

        this.arrows.push(bullet);

        this.eventDispatcher.dispatchEvent(new Event(Event.BULLET_SHOT, [bullet, peaTurret]));
    }

    public addGlueBullet(bullet: GlueBullet, splatTurret: SplatTurret): void {

        this.glueBullets.push(bullet);

        this.eventDispatcher.dispatchEvent(new Event(Event.GLUE_BULLET_SHOT, [bullet, splatTurret]));
    }

    public addGlue(glue: Glue, splatTurret: SplatTurret): void {

        this.glues.push(glue);

        this.eventDispatcher.dispatchEvent(new Event(Event.GLUE_SHOT, [glue, splatTurret]));
    }

    public addMortar(mortar: Mortar, mortarTurret: MortarTurret): void {

        this.mortars.push(mortar);

        this.eventDispatcher.dispatchEvent(new Event(Event.MORTAR_SHOT, [mortar, mortarTurret]));
    }

    public addRocket(mortar: Mortar, mortarTurret: RocketsTurret): void {

        this.mortars.push(mortar);

        this.eventDispatcher.dispatchEvent(new Event(Event.ROCKET_SHOT, [mortar, mortarTurret]));
    }

    public addMine(mine: Mine, minesTurret: MinesTurret): void {

        this.mines.push(mine);

        this.eventDispatcher.dispatchEvent(new Event(Event.MINE_SHOT, [mine, minesTurret]));
    }

    public addLaserRay(bzztTurret: BzztTurret, enemies: Enemy[]): void {

        for (let i = 0; i < enemies.length; i++) {
            enemies[i].hit(bzztTurret.damage, null, null, null, null, bzztTurret);
        }

        this.eventDispatcher.dispatchEvent(new Event(Event.LASER_SHOT, [bzztTurret, enemies]));
        this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_HIT, [enemies, null, null, null, null, true]));
    }

    public addFireRay(fireTurret: FireTurret, enemies: Enemy[]): void {

        for (let i = 0; i < enemies.length; i++) {
            enemies[i].hit(fireTurret.damage, null, null, null, fireTurret);
        }

        this.eventDispatcher.dispatchEvent(new Event(Event.FIRE_SHOT, [fireTurret, enemies]));
        this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_HIT, [enemies, null, null, null, true]));
    }

    public flagEnemyToTeleport(enemy: Enemy, teleportTurret: TeleportTurret): void {

        this.teleportedEnemies.push({enemy: enemy, teleportTurret: teleportTurret});
    }

    public onEnemyReachedExit(enemy: Enemy): void {

        const i = this.enemies.indexOf(enemy);

        if (i !== -1) {
            this.enemies.splice(i, 1);
        }

        this._score += enemy.value;
        this.remainingReward -= enemy.value;

        enemy.destroy();
        
        if (enemy.subtype.includes("boss")) {
            this._lifes = 0;
        } else {
            this._lifes -= 1;
        }

        this._bonus = Math.round(this.waveReward + Math.round(GameConstants.EARLY_BONUS_MODIFIER * Math.pow(Math.max(0, this.remainingReward), GameConstants.EARLY_BONUS_EXPONENT)));

        if (this.enemies.length === 0 && this.allEnemiesSpawned) {
            this.onNoEnemiesOnStage();
        }

        this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_REACHED_EXIT, [enemy]));
    }

    public killAllWaveEnemies(): void {

        // hacer una copia del array de enemigos
        let enemies = this.enemies.slice(0);

        // matar a todos los enemigos que están en el tablero
        for (let i = 0; i < enemies.length; i ++) {
            
            enemies[i].killInstantly();
        }

        this.enemiesSpawner.killAllWaveEnemies();

        enemies = this.enemies.slice(0);

        if(enemies.length > 0) {

            for(let i = 0; i < enemies.length; i ++) {
            
                enemies[i].killInstantly();
            }

            enemies = this.enemies.slice(0);

            if (enemies.length > 0) {
                // quedan otros por morir
                for (let i = 0; i < enemies.length; i ++) {
            
                    enemies[i].killInstantly();
                }
            }
        }

        this.onNoEnemiesOnStage();
        this.allEnemiesSpawned = true;
        this.enemiesSpawned = 0;
        this.waveEnemiesLength = 0;
    }

    public onEnemyKilled(enemy: Enemy): void {

        this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_KILLED, [enemy]));

        const i = this.enemies.indexOf(enemy);

        if (i !== -1) {
            this.enemies.splice(i, 1);
        }

        this._credits += enemy.value;
        this._creditsEarned += enemy.value;
        this._score += enemy.value;
        this.remainingReward -= enemy.value;

        if (enemy.split) {
            this.splitEnemy(enemy, .25);
            this.splitEnemy(enemy, - .25);
        }
       
        enemy.destroy();

        this._bonus = Math.round(this.waveReward + Math.round(GameConstants.EARLY_BONUS_MODIFIER * Math.pow(Math.max(0, this.remainingReward), GameConstants.EARLY_BONUS_EXPONENT)));

        if (this.enemies.length === 0 && this.allEnemiesSpawned) {
            this.onNoEnemiesOnStage();
        }        
    }

    public splitEnemy(enemy: Enemy, off: number): void {

        let newEnemy: Enemy;

        switch (enemy.type) {
            case GameConstants.ENEMY_WATER:
                newEnemy = new HealerEnemy(enemy.split, this.ticksCounter, this, enemy.path, enemy.l + off, true);
                break;
            case GameConstants.ENEMY_WIND:
                enemy = new WindEnemy(enemy.split, this.ticksCounter, this, enemy.path, enemy.l + off, true);
                    break;
            default:
                newEnemy = new Enemy(enemy.type, enemy.split, this.ticksCounter, this, enemy.path, enemy.l + off, true);
                break;
        }

        newEnemy.maxLife = newEnemy.life;

        let p = MathUtils.getPathPosition(enemy.l + off, enemy.path);

        if (!p) {
            p = {x: 0, y: 0};
        }

        this.enemies.push(newEnemy);

        this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_SPAWNED, [newEnemy, {c: p.x - .5, r: p.y - .5}]));
    }

    public upgradeTurret(id: number): boolean {

        let success = false;

        const turret = this.getTurretById(id);

        if (turret && !turret.constructing && !turret.upgrading && turret.level < turret.maxLevel && this._credits >= turret.priceUpgrade) {
            this._credits -= turret.priceUpgrade;
            turret.setToUpgrade();
            success = true;
        }

        return success;
    }

    public onTurretUpgraded(id: number): void {

        this.eventDispatcher.dispatchEvent(new Event(Event.TURRET_UPGRADED, [id]));
    }

    public onTurretReady(id: number): void {

        this.eventDispatcher.dispatchEvent(new Event(Event.TURRET_READY, [id]));
    }

    public addEventListener(type: string, listenerFunction: Function, scope: any): void {
        
        this.eventDispatcher.addEventListener(type, listenerFunction, scope);
    }

    public removeEventListener(type: string, listenerFunction): void {

        this.eventDispatcher.removeEventListener(type, listenerFunction);
    }

    private checkCollisions(): void {

        // las balas
        for (let i = 0; i < this.arrows.length; i ++) {
            
            const arrow = this.arrows[i];

            if (arrow.outOfStageBoundaries) {
                this.arrowsColliding.push(arrow);
            } else {
                
                let enemy = arrow.assignedEnemy;

                const bp1 = {x: arrow.x, y: arrow.y};
                const bp2 = arrow.getPositionNextTick();

                let enemyPosition: {x: number, y: number};
                let enemyHit: boolean;

                // no importa si el enemigo ya ha muerto la bala se marca cuando alcanza la posicion que el enemigo muerto tuvo
                if (enemy) {
                    
                    enemyPosition = {x: enemy.x, y: enemy.y + enemy.deltaYBoundingRadius};

                    let boundingRadius = enemy.life > 0 ? enemy.boundingRadius : 1.5 * enemy.boundingRadius;
                    enemyHit = MathUtils.isLineSegmentIntersectingCircle(bp1, bp2, enemyPosition, boundingRadius);

                    if (enemyHit) {
                        this.arrowsColliding.push(arrow);
                    }
                } else {
                    // es una bala que tenia asiganada un enemigo que ha sido teletransportada
                    // mirar si colisiona contra otro enemigo y en este caso reasignarselo
                    // y meterla en el array de balas a eliminar
                    for (let j = 0; j < this.enemies.length; j ++) {

                        enemy = this.enemies[j];
                        enemyPosition = {x: enemy.x, y: enemy.y + enemy.deltaYBoundingRadius};

                        enemyHit = MathUtils.isLineSegmentIntersectingCircle(bp1, bp2, enemyPosition, 1.25 * enemy.boundingRadius);

                        if (enemyHit) {
                            arrow.assignedEnemy = enemy;
                            this.arrowsColliding.push(arrow);
                            break;
                        }
                    }
                }
            }
        } 

        for (let i = 0; i < this.glueBullets.length; i ++) {
            
            const gluebullet = this.glueBullets[i];

            if (gluebullet.outOfStageBoundaries) {
                this.glueBulletsColliding.push(gluebullet);
            } else {

                let enemy = gluebullet.assignedEnemy;

                const bp1 = {x: gluebullet.x, y: gluebullet.y};
                const bp2 = gluebullet.getPositionNextTick();

                let enemyPosition: {x: number, y: number};
                let enemyHit: boolean;

                if (enemy) {

                    enemyPosition = {x: enemy.x, y: enemy.y};

                    let boundingRadius = enemy.life > 0 ? enemy.boundingRadius : 1.65 * enemy.boundingRadius;
                    enemyHit = MathUtils.isLineSegmentIntersectingCircle(bp1, bp2, enemyPosition, boundingRadius);

                    if (enemyHit) {
                        this.glueBulletsColliding.push(gluebullet);
                    }
                } else {

                    for (let j = 0; j < this.enemies.length; j ++) {

                        enemy = this.enemies[j];
                        enemyPosition = {x: enemy.x, y: enemy.y};

                        enemyHit = MathUtils.isLineSegmentIntersectingCircle(bp1, bp2, enemyPosition, 1.25 * enemy.boundingRadius);

                        if (enemyHit) {
                            gluebullet.assignedEnemy = enemy;
                            this.glueBulletsColliding.push(gluebullet);
                            break;
                        }
                    }
                }
            }
        } 

        for (let i = 0; i < this.mortars.length; i ++) {

            if (this.mortars[i].detonate) {
                this.mortarsImpacting.push(this.mortars[i]);
            }
        }

        for (let i = 0; i < this.mines.length; i ++) {

            if (this.mines[i].detonate) {
                this.minesImpacting.push(this.mines[i]);
            }
        }

        for (let i = 0; i < this.glues.length; i ++) {

            if (this.glues[i].consumed) {
                this.consumedGlues.push(this.glues[i]);
            }
        }

        for (let i = 0; i < this.enemies.length; i ++) {

            const enemy = this.enemies[i];

            enemy.affectedByGlue = false;

            for (let j = 0; j < this.glues.length; j++) {

                const glue = this.glues[j];

                if (!glue.consumed) {

                    const dx = enemy.x - glue.x;
                    const dy = enemy.y - glue.y;
    
                    const squaredDist = MathUtils.fixNumber(dx * dx + dy * dy);
                    let squaredRange = MathUtils.fixNumber(glue.range * glue.range);
    
                    if (squaredRange >= squaredDist && enemy.type !== GameConstants.ENEMY_WIND) {
                        enemy.glue(glue.intensity);
                        break; // EL EFECTO DEL PEGAMENTO NO ES ACUMULATIVO, NO HACE FALTA COMPROBAR CON MAS PEGAMENTOS
                    }
                }
            }
        }

        // el soldado suicida
        if (this.suicideSoldier && this.enemies.length > 0) {
            
            // ordenar a los enemigos por la l y pillar el primero
            const sortedEnemies = MathUtils.mergeSort(this.enemies, (e1, e2) => (e1.l - e2.l) > 0);

            for (let i = 0; i < sortedEnemies.length; i++) {
                const d = Math.sqrt((sortedEnemies[i].x - this.suicideSoldier.x) * (sortedEnemies[i].x - this.suicideSoldier.x) + (sortedEnemies[i].y - this.suicideSoldier.y) * (sortedEnemies[i].y - this.suicideSoldier.y));
                if (d < .75) {
                    this.suicideSoldier.detonateBomb = true;
                    break;
                }
            }
        }
    }

    private removeProjectilesAndAccountDamage(): void {

        // las balas
        for (let i = 0; i < this.arrowsColliding.length; i ++) {

            const bullet = this.arrowsColliding[i];
            const enemy = bullet.assignedEnemy;

            // si el enemigo ya ha muerto o la bala ha salido del tablero
            if (bullet.outOfStageBoundaries || enemy.life === 0) {
                this.eventDispatcher.dispatchEvent(new Event(Event.REMOVE_BULLET, [bullet]));
            } else {
                this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_HIT, [[enemy], bullet]));
                enemy.hit(bullet.damage, bullet);
            }

            const index = this.arrows.indexOf(bullet);
            this.arrows.splice(index, 1);
            bullet.destroy();
        }

        this.arrowsColliding.length = 0;

        // las balas de pegamento
        for (let i = 0; i < this.glueBulletsColliding.length; i ++) {

            const glueBullet = this.glueBulletsColliding[i];
            const enemy = glueBullet.assignedEnemy;

            // si el enemigo ya ha muerto o la bala ha salido del tablero
            if (glueBullet.outOfStageBoundaries || enemy.life === 0) {
                this.eventDispatcher.dispatchEvent(new Event(Event.REMOVE_GLUE_BULLET, [glueBullet]));
            } else {
                this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_BZZT_HIT, [[enemy], glueBullet]));
                enemy.hitByBzztBullet(glueBullet.intensity, glueBullet.durationTicks);
            }

            const index = this.glueBullets.indexOf(glueBullet);
            this.glueBullets.splice(index, 1);
            glueBullet.destroy();
        }

        this.glueBulletsColliding.length = 0;
        
        // los morteros
        for (let i = 0; i < this.mortarsImpacting.length; i ++) {

            const mortar = this.mortarsImpacting[i];

            const hitEnemiesData: {enemy: Enemy, damage: number} [] = MathUtils.getEnemiesWithinExplosionRange(this.enemies, {x: mortar.x, y: mortar.y}, mortar.explosionRange, mortar.damage);
            const hitEnemies: Enemy[] = [];

            if (hitEnemiesData.length > 0) {

                for (let j = 0; j < hitEnemiesData.length; j ++) {

                    const enemy = hitEnemiesData[j].enemy;

                    if (enemy.life > 0) {
                        enemy.hit(hitEnemiesData[j].damage, null, mortar);
                        hitEnemies.push(enemy);
                    }
                }
            }

            this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_HIT, [hitEnemies, null, mortar]));

            const index = this.mortars.indexOf(mortar);
            this.mortars.splice(index, 1);

            mortar.destroy();
        }

        this.mortarsImpacting.length = 0;

        // las minas
        for (let i = 0; i < this.minesImpacting.length; i ++) {

            const mine = this.minesImpacting[i];

            const hitEnemiesData: {enemy: Enemy, damage: number} [] = mine.getEnemiesWithinExplosionRange();
            const hitEnemies: Enemy[] = [];

            if (hitEnemiesData.length > 0) {

                for (let j = 0; j < hitEnemiesData.length; j ++) {

                    const enemy = hitEnemiesData[j].enemy;

                    if (enemy.life > 0) {
                        enemy.hit(hitEnemiesData[j].damage, null, null, mine);
                        hitEnemies.push(enemy);
                    }
                }
            }

            this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_HIT, [hitEnemies, null, null, mine]));

            const index = this.mines.indexOf(mine);
            this.mines.splice(index, 1);

            let turret = mine.turret as MinesTurret;

            if (turret) {
                turret.numMines--;
            }

            mine.destroy();
        }

        this.minesImpacting.length = 0;

        // los pegamentos
        for (let i = 0; i < this.consumedGlues.length; i ++) {

            const glue = this.consumedGlues[i];

            const index = this.glues.indexOf(glue);
            this.glues.splice(index, 1);

            this.eventDispatcher.dispatchEvent(new Event(Event.GLUE_CONSUMED, [glue]));
            glue.destroy();
        }  

        this.consumedGlues.length = 0;

        // LOS SOLDADOS SUICIDAS
        if (this.suicideSoldier && this.suicideSoldier.detonateBomb) {

            const hitEnemiesData: {enemy: Enemy, damage: number} [] = MathUtils.getEnemiesWithinExplosionRange(this.enemies, {x: this.suicideSoldier.x, y: this.suicideSoldier.y}, this.suicideSoldier.explosionRange,  this.suicideAttackDamage );
            const hitEnemies: Enemy[] = [];

            if (hitEnemiesData.length > 0) {

                for (let j = 0; j < hitEnemiesData.length; j ++) {

                    const enemy = hitEnemiesData[j].enemy;

                    if (enemy.life > 0) {
                        enemy.hit(hitEnemiesData[j].damage);
                        hitEnemies.push(enemy);
                    }
                }
            }

            this.eventDispatcher.dispatchEvent(new Event(Event.SUICIDE_SOLDIER_EXPLODED, [this.suicideSoldier, hitEnemies]));
            
            this.suicideSoldierCooldownCounter = 0;
            this.suicideSoldierCoolingDown = true;

            this.suicideSoldier.destroy();
            this.suicideSoldier = null;
        }
    }

    private teleport(): void {

        const teleportedEnemiesData: {enemy: Enemy, teleportTurret: TeleportTurret} [] = [];
        
        for (let i = 0; i < this.teleportedEnemies.length; i ++) {
            const enemy = this.teleportedEnemies[i].enemy;
            enemy.teleport(this.teleportedEnemies[i].teleportTurret.teleportDistance);
            teleportedEnemiesData.push({enemy: enemy, teleportTurret: this.teleportedEnemies[i].teleportTurret});
            
            // ¿hay balas que tenian asignadas este enemigo?
            for (let i = 0; i < this.arrows.length; i ++) {

                const bullet = this.arrows[i];

                if (bullet.assignedEnemy && bullet.assignedEnemy.id === enemy.id) {
                    bullet.assignedEnemy = null;
                }
            }

            for (let i = 0; i < this.glueBullets.length; i ++) {

                const glueBullet = this.glueBullets[i];

                if (glueBullet.assignedEnemy && glueBullet.assignedEnemy.id === enemy.id) {
                    glueBullet.assignedEnemy = null;
                }
            }
        }

        this.teleportedEnemies.length = 0;

        if (teleportedEnemiesData.length > 0) {
            this.eventDispatcher.dispatchEvent(new Event(Event.ENEMIES_TELEPORTED, [teleportedEnemiesData]));
        }
    }

    private spawnEnemies(): void {

        let enemy = this.enemiesSpawner.getEnemy();

        while (enemy) {

            this.enemiesSpawned++;

            if (this.enemiesSpawned === this.waveEnemiesLength) {
                this.allEnemiesSpawned = true;
                this.enemiesSpawned = 0;
                this.waveEnemiesLength = 0;
            }

            this.enemies.push(enemy);
            this.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_SPAWNED, [enemy, enemy.path[0]]));

            enemy = this.enemiesSpawner.getEnemy();
        }
    }

    private onNoEnemiesOnStage(): void {

        this.noEnemiesOnStage = true;

        // this._credits += this._bonus;
        // this._creditsEarned += this._bonus;
        // this._bonus = 0;

        this.eventDispatcher.dispatchEvent(new Event(Event.NO_ENEMIES_ON_STAGE));
    }

    private getTurretById(id: number): Turret {

        let turret: Turret = null;

        for (let i = 0; i < this.turrets.length; i ++) {
            if (this.turrets[i].id === id) {
                turret = this.turrets[i];
                break;
            }
        }

        return turret;
    }

    // GETTERS Y SETTERS
    public get credits(): number {
        
        return this._credits;
    }

    public get creditsEarned(): number {
        
        return this._creditsEarned;
    }

    public get bonus(): number {
        
        return this._bonus;
    }

    public get ticksCounter(): number {

        return this._ticksCounter;
    }

    public get score(): number {

        return this._score;
    }

    public get gameOver(): boolean {

        return this._gameOver;
    }

    public get win(): boolean {

        return this._win;
    }

    public get lifes(): number {
        
        return this._lifes;
    }

    public get round(): number {
        
        return this._round;
    }

    public get timeStep(): number {

        return this._timeStep;
    }

    public set timeStep(value: number) {

        this._timeStep = value;
    }

    public get paused(): boolean {

        return this._paused;
    }

    public set paused(value: boolean) {

        this._paused = value;
    }

    public get version(): string {

        return this._version;
    }

    public set version(value: string) {

        this._version = value;
    }
}
